/**
 * ChannelSettingsModal - Create/edit channel modal.
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { chatChannelsApi } from '../../services/chatApi';
import { useChat } from '../../contexts/ChatContext';
import type { Channel } from '../../types/chat';

interface ChannelSettingsModalProps {
  channel?: Channel | null;
  parentId?: string | null;
  onClose: () => void;
}

const ChannelSettingsModal: React.FC<ChannelSettingsModalProps> = ({ channel, parentId, onClose }) => {
  const { t } = useTranslation('chat');
  const { refreshChannels } = useChat();
  const [name, setName] = useState(channel?.name || '');
  const [description, setDescription] = useState(channel?.description || '');
  const [channelType, setChannelType] = useState(channel?.channel_type || 'public');
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      if (channel) {
        await chatChannelsApi.update(channel.id, { name, description });
      } else {
        await chatChannelsApi.create({
          name,
          slug,
          description,
          channel_type: channelType,
          parent_id: parentId,
        });
      }
      await refreshChannels();
      onClose();
    } catch (e) {
      console.error('Failed to save channel:', e);
    } finally {
      setSaving(false);
    }
  }, [name, description, channelType, channel, parentId, refreshChannels, onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 text-gray-900">
        <h2 className="text-lg font-semibold mb-4">
          {channel ? t('editChannel') : t('createChannel')}
        </h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('channelName')}</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('channelNamePlaceholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('description')}</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="w-full border rounded px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          {!channel && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('channelType')}</label>
              <select
                value={channelType}
                onChange={e => setChannelType(e.target.value as 'public' | 'private' | 'direct_message' | 'announcement')}
                className="w-full border rounded px-3 py-2 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="public">{t('public')}</option>
                <option value="private">{t('private')}</option>
                <option value="announcement">{t('announcement')}</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            {t('cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={!name.trim() || saving}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? t('saving') : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChannelSettingsModal;
